import {useMemo, useState} from "react";
import {Box, Paper, Tab, Typography} from "@mui/material";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {Navigate} from "react-router-dom";
import useApiCall from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../../components/LoadingContent";
import {ComplaintStatisticsResponse} from "../../data/internal-affairs/ComplaintStatisticsResponse";
import useUser from "../../hooks/useUser";
import {StatisticsFilterBar, StatisticsFilters, defaultFilters, buildStatsUrl} from "../../components/internal-affairs/statistics/StatisticsFilterBar";
import {ComplaintStatisticsTab} from "../../components/internal-affairs/statistics/ComplaintStatisticsTab";
import {InvestigatorStatisticsTab} from "../../components/internal-affairs/statistics/InvestigatorStatisticsTab";
import {CCOverviewTab} from "../../components/internal-affairs/statistics/CCOverviewTab";

export const ComplaintStatisticsPage = () => {
    const user = useUser()
    const [tab, setTab] = useState("complaints")
    const [filters, setFilters] = useState<StatisticsFilters>(defaultFilters)

    const url = useMemo(() => buildStatsUrl(filters), [filters])
    const data = useApiCall<ComplaintStatisticsResponse>({url})

    if (!user.isLoading && user.user && !user.hasPermissions(["internalAffairs:viewAllComplaints"])) {
        return <Navigate to="/" replace/>
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <Typography variant="h5" fontWeight={600}>Complaint Statistics</Typography>

            <StatisticsFilterBar
                filters={filters}
                investigators={data.data?.investigatorStats ?? []}
                onChange={setFilters}
            />

            <LoadingContent isLoading={user.isLoading || data.isLoading || !data.data}>
                {data.data && (
                    <TabContext value={tab}>
                        <Paper variant="outlined">
                            <Box sx={{borderBottom: 1, borderColor: "divider", px: 1}}>
                                <TabList variant="scrollable" onChange={(_, v) => setTab(v)}>
                                    <Tab label="Complaint Statistics" value="complaints"/>
                                    <Tab label="Investigator Statistics" value="investigators"/>
                                    {data.data.ccMemberStats && <Tab label="CC Overview" value="cc"/>}
                                </TabList>
                            </Box>
                            <TabPanel value="complaints">
                                <ComplaintStatisticsTab d={data.data} hasDateFilter={!!filters.dateFrom || !!filters.dateTo}/>
                            </TabPanel>
                            <TabPanel value="investigators">
                                <InvestigatorStatisticsTab stats={data.data.investigatorStats}/>
                            </TabPanel>
                            {data.data.ccMemberStats && (
                                <TabPanel value="cc">
                                    <CCOverviewTab stats={data.data.ccMemberStats}/>
                                </TabPanel>
                            )}
                        </Paper>
                    </TabContext>
                )}
            </LoadingContent>
        </Box>
    )
}
